import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

/**
 * The landing page's motion, and **only** the landing page's.
 *
 * `Reveal` used to live in `shared/ui`, which put a 700ms scroll reveal one
 * import away from every screen in the product. (Its sibling, a counting
 * number, left with the unsourced figures it animated - landing plan, 2026-10-13.)
 * The UI audit of 2026-09-21 separated the two jobs: a signed-in student opens
 * the app ten times a day and pays for every animation on that path, while a
 * visitor reads this page once and an entrance is what tells them the product
 * is looked after. Same code, opposite verdicts - so the code lives with the
 * page it is right for.
 *
 * Do not re-export this from `shared/`. If a signed-in screen wants an
 * entrance, the answer is a 200ms stagger on first mount, not this.
 */

type RevealState = 'hidden' | 'visible' | 'settled';

export function Reveal({
  children,
  delay = 0,
  className = '',
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [state, setState] = useState<RevealState>('hidden');

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Reduced motion, or a browser with no IntersectionObserver: the content is
    // simply there. A section left at opacity 0 is worse than no entrance.
    if (
      typeof IntersectionObserver !== 'function' ||
      (typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    ) {
      setState('settled');
      return;
    }
    // Already on screen at first paint: animate now rather than waiting for an
    // intersection that has already happened.
    if (node.getBoundingClientRect().top <= window.innerHeight) {
      setState('visible');
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setState('visible');
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -48px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const classes = [
    className,
    'landing-reveal',
    state === 'visible' ? 'is-visible' : '',
    state === 'settled' ? 'is-settled' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={ref}
      style={delay && state === 'visible' ? { animationDelay: `${delay}ms` } : undefined}
      className={classes}
      onAnimationEnd={() => setState('settled')}
    >
      {children}
    </div>
  );
}
